import React, { useState, useEffect } from 'react';
import { getExpenseDetails } from './ExpenseDataServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowLeft, faFileInvoice, faFileDownload, faChevronDown, faChevronUp, faEye, faXmark, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getDoc, query, where, collection } from '../firebaseStubs.js';

// Ajoutez cette ligne pour définir validatedRef
const validatedRef = collection('validatedExpenses');

const HistoriqueDepenses = ({ projectId, onBack }) => {
  const [validatedExpenses, setValidatedExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupDepenses, setGroupDepenses] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [chefChantierName, setChefChantierName] = useState('N/A');
  const [groupedDepenses, setGroupedDepenses] = useState({});
  const [expandedRubriques, setExpandedRubriques] = useState({});
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');

  useEffect(() => {
    loadValidatedExpenses();
    fetchChefChantierName();
  }, [projectId]);

  const fetchChefChantierName = async () => {
    try {
      // Définir la référence du projet à partir de l'ID du projet
      const projectRef = collection('projects').doc(projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (projectSnap.exists()) {
        const projectData = projectSnap.data();
        if (projectData.responsableChantier) {
          setChefChantierName(projectData.responsableChantier);
        }
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du chef de chantier:", err);
    }
  };

  const loadValidatedExpenses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const q = query(validatedRef, where('projectId', '==', projectId));
      
      const validated = [];
        // TODO: Remplir validated avec les données récupérées
        // validated.push(...);
      
      // Trier par date de validation (du plus récent au plus ancien)
      const sortedValidated = validated.sort((a, b) => {
        const dateA = a.dateValidation?.toDate?.() || new Date(0);
        const dateB = b.dateValidation?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      
      setValidatedExpenses(sortedValidated);
    } catch (err) {
      console.error("Erreur lors du chargement des dépenses validées:", err);
      setError("Impossible de charger l'historique des dépenses validées. Veuillez réessayer plus tard.");
    } finally {
      setLoading(false);
    }
  };

  const toggleRubrique = (rubrique) => {
    setExpandedRubriques(prev => ({
      ...prev,
      [rubrique]: !prev[rubrique]
    }));
  };

  const showDescription = (description) => {
    setSelectedDescription(description || 'Aucune description disponible');
    setShowDescriptionModal(true);
  };

  const viewGroupDetails = async (group) => {
    if (selectedGroupId === group.id) {
      setSelectedGroupId(null);
      setGroupedDepenses({});
      return;
    }
    
    setSelectedGroupId(group.id);
    setLoadingDetails(true);
    
    try {
      let depenses = await getExpenseDetails(group.id);
      depenses = depenses.map(depense => ({
        ...depense,
        montant: depense.montant !== undefined && depense.montant !== null && depense.montant !== ''
          ? Number(depense.montant)
          : 0
      }));

      setGroupDepenses(depenses);

      const grouped = depenses.reduce((acc, depense) => {
        const rubrique = depense.rubrique;
        if (!acc[rubrique]) {
          acc[rubrique] = {
            total: 0,
            sousRubriques: {}
          };
        }
        
        const sousRubrique = depense.sousRubrique || 'Non spécifié';
        if (!acc[rubrique].sousRubriques[sousRubrique]) {
          acc[rubrique].sousRubriques[sousRubrique] = [];
        }
        
        acc[rubrique].sousRubriques[sousRubrique].push(depense);
        acc[rubrique].total += depense.montant;
        
        return acc;
      }, {});

      setGroupedDepenses(grouped);

      const initialExpandedState = Object.keys(grouped).reduce((acc, rubrique) => {
        acc[rubrique] = true;
        return acc;
      }, {});
      setExpandedRubriques(initialExpandedState);

    } catch (err) {
      console.error("Erreur lors du chargement des détails des dépenses:", err);
      setGroupDepenses([]);
      setGroupedDepenses({});
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSelectedGroup = () => {
    return validatedExpenses.find(group => group.id === selectedGroupId);
  };

  const getSeparatedRubriqueSousRubrique = (groups) => {
    const separated = [];
    
    groups.forEach(group => {
      const rubrique = group.rubrique || 'Non spécifié';
      const sousRubrique = group.sousRubrique || 'Non spécifié';
      const montant = group.montant || 0;
      
      const existingIndex = separated.findIndex(item => 
        item.rubrique === rubrique && item.sousRubrique === sousRubrique
      );
      
      if (existingIndex !== -1) {
        separated[existingIndex].montant += montant;
        separated[existingIndex].groups.push(group);
      } else {
        separated.push({
          rubrique,
          sousRubrique,
          montant,
          groups: [group]
        });
      }
    });
    
    const groupedByRubrique = {};
    separated.forEach(item => {
      if (!groupedByRubrique[item.rubrique]) {
        groupedByRubrique[item.rubrique] = [];
      }
      groupedByRubrique[item.rubrique].push(item);
    });
    
    return groupedByRubrique;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6 bg-red-100 text-red-700 rounded-lg">
        <p>{error}</p>
        <button 
          onClick={loadValidatedExpenses}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const separatedRubriques = getSeparatedRubriqueSousRubrique(validatedExpenses);

  return (
    <div className="flex-1 overflow-auto p-6 pl-60 pt-20 animate-slide-in-right">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Historique des dépenses validées</h1>
            <p className="text-gray-600">Toutes les dépenses validées par le Directeur Général</p>
          </div>
          <button 
            onClick={onBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Retour au tableau de bord
          </button>
        </div>

        {/* En-tête avec statistiques */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-3" />
                Dépenses Validées
              </h2>
              <p className="text-green-100 mt-1">
                {validatedExpenses.length} demande{validatedExpenses.length > 1 ? 's' : ''} validée{validatedExpenses.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-green-100">Montant total validé</p>
              <p className="text-2xl font-bold">
                {validatedExpenses.reduce((total, exp) => total + (exp.montant || 0), 0).toLocaleString()} CFA
              </p>
            </div>
          </div>
        </div>
        
        {validatedExpenses.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <FontAwesomeIcon 
              icon={faCheckCircle} 
              className="text-green-400 text-5xl mb-4" 
            />
            <h3 className="text-xl font-medium text-gray-700">
              Aucune dépense validée
            </h3>
            <p className="text-gray-500 mt-2">
              Les dépenses validées par le DG apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Affichage séparé par rubrique */}
            {Object.entries(separatedRubriques).map(([rubrique, sousRubriques], rubriquIndex) => (
              <div key={rubrique} className="bg-white rounded-lg shadow overflow-hidden">
                {/* En-tête de la rubrique */}
                <div className="bg-green-600 text-white px-6 py-4">
                  <h2 className="text-xl font-bold">{rubrique}</h2>
                  <p className="text-green-100 text-sm">
                    {sousRubriques.reduce((total, item) => total + item.montant, 0).toLocaleString()} CFA au total
                  </p>
                </div>
                
                {/* Tableau des sous-rubriques pour cette rubrique */}
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sous-rubrique
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nombre de demandes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Chef de chantier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date de validation
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sousRubriques.map((item, index) => (
                        <tr key={`${rubrique}-${item.sousRubrique}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              {item.sousRubrique}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.montant.toLocaleString()} CFA
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.groups.length} demande{item.groups.length > 1 ? 's' : ''}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {chefChantierName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex flex-col gap-1">
                              {item.groups.map((group) => (
                                <span key={`validation-${group.id}`} className="text-xs">
                                  {formatDate(group.dateValidation)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex flex-col gap-1">
                              {item.groups.map((group) => (
                                group.description ? (
                                  <button
                                    key={`desc-${group.id}`}
                                    onClick={() => showDescription(group.description)}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                    Lire
                                  </button>
                                ) : (
                                  <span key={`no-desc-${group.id}`} className="text-xs text-gray-400">
                                    Aucune description
                                  </span>
                                )
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-col gap-1">
                              {item.groups.map((group) => (
                                <button
                                  key={group.id}
                                  onClick={() => viewGroupDetails(group)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  <FontAwesomeIcon icon={faEye} className="mr-1" />
                                  {selectedGroupId === group.id ? 'Masquer' : `Voir ${formatDate(group.dateCreation || group.dateValidation).split(' ')[0]}`}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Modal pour afficher la description */}
            {showDescriptionModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Description de la demande</h3>
                    <button 
                      onClick={() => setShowDescriptionModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-xl" />
                    </button>
                  </div>
                  <div className="px-6 py-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedDescription}</p>
                    </div>
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button 
                      onClick={() => setShowDescriptionModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Section des détails (affichée en modal) */}
            {selectedGroupId && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto pt-10">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mb-10 mx-4">
                  <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800">
                      Détails de la demande validée
                    </h3>
                    <button 
                      onClick={() => setSelectedGroupId(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-2xl" />
                    </button>
                  </div>

                  <div className="px-6 py-6">
                    {loadingDetails ? (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const group = getSelectedGroup();
                          
                          return (
                            <>
                              <div className="bg-white rounded-lg p-4 mb-6">
                                {/* Statut de validation */}
                                <div className="p-4 rounded-lg mb-4 bg-green-100">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                                    <span className="font-semibold text-green-800">
                                      Demande validée par le Directeur Général
                                    </span>
                                  </div>
                                  <p className="text-sm text-green-700 mt-1">
                                    Date de validation : {formatDate(group.dateValidation)}
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                  <div>
                                    <p className="text-sm text-gray-600">Demande créée le</p>
                                    <p className="font-medium">{formatDate(group.dateCreation)}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-600">Montant dépense</p>
                                    <p className="font-medium text-lg">{group.montant?.toLocaleString() || 0} CFA</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-600">Chef de chantier</p>
                                    <p className="font-medium">{chefChantierName}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-600">Projet</p>
                                    <p className="font-medium">{group.projectNom || 'Non spécifié'}</p>
                                  </div>
                                </div>

                                {group.description && (
                                  <div className="p-3 bg-blue-50 rounded mt-3">
                                    <p className="text-sm text-gray-600 mb-1">Description de la demande:</p>
                                    <p className="text-gray-800">{group.description}</p>
                                  </div>
                                )}
                              </div>
                              
                              <h4 className="text-md font-semibold mb-4">Liste des dépenses</h4>
                              
                              {Object.keys(groupedDepenses).length > 0 ? (
                                <div className="grid grid-cols-1 gap-4">
                                  {Object.entries(groupedDepenses).map(([rubrique, data]) => (
                                    <div key={rubrique} className="bg-white rounded-lg p-4 mb-4">
                                      <div 
                                        className="flex justify-between items-center cursor-pointer"
                                        onClick={() => toggleRubrique(rubrique)}
                                      >
                                        <div className="flex-grow">
                                          <div className="flex items-center">
                                            <h5 className="font-semibold text-lg text-green-700">{rubrique}</h5>
                                          </div>
                                          <p className="text-sm text-gray-600 mt-1">
                                            Total: {data.total.toLocaleString()} CFA
                                          </p>
                                        </div>
                                        <FontAwesomeIcon 
                                          icon={expandedRubriques[rubrique] ? faChevronUp : faChevronDown} 
                                          className="text-green-600 transition-transform duration-200" 
                                        />
                                      </div>
                                      
                                      {expandedRubriques[rubrique] && (
                                        <div className="mt-4 space-y-3">
                                          {Object.entries(data.sousRubriques).map(([sousRubrique, depenses]) => (
                                            <div key={sousRubrique} className="border-l-4 border-gray-200 pl-4">
                                              <h6 className="font-medium text-gray-800 mb-2">{sousRubrique}</h6>
                                              <div className="overflow-x-auto">
                                                <table className="min-w-full">
                                                  <thead className="bg-gray-50">
                                                    <tr>
                                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Date
                                                      </th>
                                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Montant
                                                      </th>
                                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Description
                                                      </th>
                                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Justificatif
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                    {depenses.map((depense, index) => (
                                                      <tr key={`${depense.id}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                          {formatDate(depense.dateCreation)}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                          {depense.montant.toLocaleString()} CFA
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-gray-900 max-w-xs">
                                                          <div className="truncate" title={depense.description}>
                                                            {depense.description || 'Aucune description'}
                                                          </div>
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                          {depense.justificatif ? (
                                                            <a 
                                                              href={depense.justificatif} 
                                                              target="_blank" 
                                                              rel="noopener noreferrer"
                                                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-600 bg-green-100 hover:bg-green-200"
                                                            >
                                                              <FontAwesomeIcon icon={faFileDownload} className="mr-1" />
                                                              Télécharger
                                                            </a>
                                                          ) : (
                                                            <span className="text-gray-400 text-xs">Aucun</span>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                              <div className="mt-2 text-sm text-gray-600">
                                                Sous-total: {depenses.reduce((sum, d) => sum + d.montant, 0).toLocaleString()} CFA
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <FontAwesomeIcon icon={faFileInvoice} className="text-gray-400 text-4xl mb-3" />
                                  <p className="text-gray-600">Aucune dépense trouvée pour cette demande</p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
                    <button 
                      onClick={() => setSelectedGroupId(null)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoriqueDepenses;